import Contracts = require('@tokenysolutions/t-rex');
import { BigNumber, Contract, providers, Signer } from 'ethers';

import { contractFactory, OverrideTransactionsInterface } from '../utils/utils';

/**
 * Deploys ClaimTopicsRegistry on Ethereum's Blockchain
 * @param {{signer: Signer; overrides: OverrideTransactionsInterface}} contract
 * @returns {Promise<Contract>}
 */
export async function deployClaimTopics(contract: { signer: Signer; overrides: OverrideTransactionsInterface }): Promise<Contract> {
  const factory = contractFactory({
    abi: Contracts.contracts.ClaimTopicsRegistry.abi,
    bytecode: Contracts.contracts.ClaimTopicsRegistry.bytecode,
    signer: contract.signer,
  });
  const deploy = await factory.deploy(contract.overrides);
  return await deploy.deployed();
}

/**
 * Adds a trusted claim topic (For example: KYC=1, AML=2).
 * @param {{claimTopic: number; registry: Contract; overrides: OverrideTransactionsInterface}} tx
 * @returns {Promise<TransactionResponse>}
 */
export async function addClaimTopic(tx: {
  claimTopic: number;
  registry: Contract;
  overrides: OverrideTransactionsInterface;
}): Promise<providers.TransactionResponse> {
  return await tx.registry.addClaimTopic(tx.claimTopic, tx.overrides);
}

/**
 * Removes a trusted claim topic (For example: KYC=1, AML=2).
 * @param {{claimTopic: number; registry: Contract; overrides: OverrideTransactionsInterface}} tx
 * @returns {Promise<TransactionResponse>}
 */
export async function removeClaimTopic(tx: {
  claimTopic: number;
  registry: Contract;
  overrides: OverrideTransactionsInterface;
}): Promise<providers.TransactionResponse> {
  return await tx.registry.removeClaimTopic(tx.claimTopic, tx.overrides);
}

/**
 * Gets all the required Claim Topics.
 * @param {{registry: Contract}} tx
 * @returns {Promise<number[]>}
 */
export async function getClaimTopics(tx: { registry: Contract }): Promise<number[]> {
  return Promise.all(
    (await tx.registry.getClaimTopics()).map((claimTopic: BigNumber) => {
      return claimTopic.toNumber();
    }),
  );
}

/**
 * Transfers the Ownership of the Claim Topics Registry to a new Owner.
 * @param {{newOwner: string; registry: Contract; overrides: OverrideTransactionsInterface}} tx
 * @returns {Promise<TransactionResponse>}
 */
export async function transferClaimTopicsRegistryOwnership(tx: {
  newOwner: string;
  registry: Contract;
  overrides: OverrideTransactionsInterface;
}): Promise<providers.TransactionResponse> {
  return await tx.registry.transferOwnershipOnClaimTopicsRegistryContract(tx.newOwner, tx.overrides);
}

/**
 * Returns the Owner of the ClaimTopicsRegistry.
 * @param {{registry: Contract; overrides: OverrideTransactionsInterface}} tx
 * @returns {Promise<TransactionResponse>}
 */
export async function getOwnerOfClaimTopicsRegistry(tx: { registry: Contract }): Promise<string> {
  return await tx.registry.owner();
}
